/**
 * The measurements somebody has taken on one model, kept between visits.
 *
 * ## Why a list at all
 *
 * The panel measured one pair at a time and forgot it the moment a third point
 * was picked. That is right for the act of measuring and wrong for the reason
 * people measure: checking three dimensions of an object against a drawing
 * means holding three numbers, and a screen that holds one turns that into
 * paper and a pen.
 *
 * So a measured pair can be kept, and kept ones are M1, M2, M3 - ordinal,
 * short enough to say out loud, and stable while the list is open.
 *
 * ## Where it lives, and why not the server
 *
 * `localStorage`, per model. **Calibration is different and is persisted on
 * the server**, because it is a property of the reconstruction that every
 * screen and every export has to agree about; a list of things one person
 * measured on one afternoon is a working note. Storing it on a user row would
 * also mean somebody's phone inheriting a workstation's scratch list.
 *
 * That trade has a cost and it is stated on the screen rather than hidden:
 * these are kept in this browser, and clearing site data clears them.
 *
 * ## What is stored, and what is not
 *
 * The two points and the provenance of the surface each landed on - never the
 * *converted* length. A measurement recorded as "84.6 mm" would be wrong the
 * moment somebody recalibrated the model, and silently so. Model-space points
 * survive a recalibration and are re-converted through the current scale, so
 * a corrected calibration corrects every kept measurement with it.
 */

import type { SurfaceProvenance } from "@/api/types";
import type { Vec3 } from "./viewerCamera";

export interface KeptMeasurement {
  /** Stable while the list is open; regenerated per browser, never a server id. */
  id: string;
  points: [Vec3, Vec3];
  /** Where each end's surface came from, so a warning survives with the number. */
  provenance: [SurfaceProvenance, SurfaceProvenance];
  /** The distance in model space. Converted on display, never stored converted. */
  modelLength: number;
  /** What the person called it, if anything. */
  note: string;
  /** Epoch milliseconds, for the order. */
  at: number;
}

/** How many one model keeps. */
const LIMIT = 24;

const keyFor = (jobId: string): string => `sentycap.viewer.measurements.${jobId}`;

/** M1, M2, M3 - the position in the list, not a stored field. */
export const measurementLabel = (index: number): string => `M${index + 1}`;

function isVec3(value: unknown): value is Vec3 {
  return (
    Array.isArray(value) &&
    value.length === 3 &&
    value.every((n) => typeof n === "number" && Number.isFinite(n))
  );
}

const PROVENANCES = new Set([
  "observed",
  "weakly_observed",
  "ai_completed",
  "unverified",
]);

/**
 * Read this model's kept measurements.
 *
 * Every field is checked individually rather than the object trusted: the
 * stored value outlives this build, and a release that adds a field must read
 * last week's entries without it. Anything that does not check out is dropped
 * rather than repaired - a half-understood measurement is a number somebody
 * would act on.
 */
export function readMeasurements(jobId: string): KeptMeasurement[] {
  try {
    const raw = window.localStorage.getItem(keyFor(jobId));
    if (!raw) return [];
    const stored = JSON.parse(raw) as unknown;
    if (!Array.isArray(stored)) return [];
    return stored
      .filter((entry): entry is KeptMeasurement => {
        const item = entry as Partial<KeptMeasurement>;
        return (
          typeof item?.id === "string" &&
          Array.isArray(item.points) &&
          item.points.length === 2 &&
          isVec3(item.points[0]) &&
          isVec3(item.points[1]) &&
          typeof item.modelLength === "number" &&
          Number.isFinite(item.modelLength) &&
          Array.isArray(item.provenance) &&
          item.provenance.length === 2 &&
          item.provenance.every((p) => PROVENANCES.has(p as string))
        );
      })
      .slice(0, LIMIT);
  } catch {
    // A private window, cleared site data, a browser that refuses storage.
    // An empty list is the correct reading of "nothing was kept".
    return [];
  }
}

export function writeMeasurements(jobId: string, items: KeptMeasurement[]): void {
  try {
    window.localStorage.setItem(
      keyFor(jobId),
      JSON.stringify(items.slice(0, LIMIT)),
    );
  } catch {
    // Kept for this session already. A browser refusing storage is not worth
    // interrupting somebody who is measuring.
  }
}

/** Add one, newest first, bounded. */
export function keepMeasurement(
  items: readonly KeptMeasurement[],
  measurement: Omit<KeptMeasurement, "id" | "at">,
): KeptMeasurement[] {
  const entry: KeptMeasurement = {
    ...measurement,
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    at: Date.now(),
  };
  return [entry, ...items].slice(0, LIMIT);
}

export const dropMeasurement = (
  items: readonly KeptMeasurement[],
  id: string,
): KeptMeasurement[] => items.filter((item) => item.id !== id);
